import type { FallbackRender } from "@sentry/react";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import ReactDOM from "react-dom/client";
import "./styles/index.css";
import "./i18n/config";
import { cleanupStaleCollabDatabases } from "@planvera/collab-client/cleanup-stale-collab-databases";
import { initSentry } from "./lib/sentry";
import { routeTree } from "./routeTree.gen";
import { orpc, queryClient } from "./utils/orpc";

void cleanupStaleCollabDatabases();

const router = createRouter({
	routeTree,
	defaultPreload: "intent",
	context: { orpc, queryClient },
});

initSentry(router);

const RootErrorFallback: FallbackRender = ({ error, resetError }) => {
	return (
		<div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center text-foreground">
			<div>
				<p className="text-lg font-semibold">Something went wrong.</p>
				<p className="text-sm text-muted-foreground">
					The planner encountered an unexpected error. Please reload.
				</p>
			</div>
			<pre className="max-w-2xl overflow-auto rounded-md bg-muted px-4 py-3 text-left text-xs">
				{error instanceof Error ? error.message : "Unknown error"}
			</pre>
			<button
				className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow transition hover:bg-primary/90"
				onClick={() => {
					resetError();
					window.location.reload();
				}}
				type="button"
			>
				Reload
			</button>
		</div>
	);
};

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}

const rootElement = document.getElementById("app");

if (!rootElement) {
	throw new Error("Root element not found");
}

if (!rootElement.innerHTML) {
	const root = ReactDOM.createRoot(rootElement);
	root.render(
		<Sentry.ErrorBoundary fallback={RootErrorFallback}>
			<QueryClientProvider client={queryClient}>
				<RouterProvider router={router} />
			</QueryClientProvider>
		</Sentry.ErrorBoundary>,
	);
}
